import type { ReactNode } from 'react'
import { Icon } from './icons'

/** 一个页签的元信息 */
export type WorkflowTab = {
  id: string
  /** 页签标题，越短越好（一行放得下 2-3 个） */
  title: string
  /** 悬停 / 无障碍补充说明 */
  desc?: string
  /**
   * 这一页**已经齐了**（必填项都有值）→ 序号徽标换成对勾。
   *
   * ## 为什么加这个（2026-10-10）
   *
   * 产品经理视角自查发现的真问题：本页有一条**硬前置** ——
   * 生成视频只认那张九宫格分镜图（见 VideoStudio 的 `sbGateReason`）。
   * 可页签条上`1 输出参数 / 2 内容与场景 / 3 分镜参考图`三个页签**长得一模一样**，
   * 用户填完前两页直接点生成，才在确认弹窗里看到红字「没有分镜图」。
   * 「按之前就该看得见」跟「按完才知道」是完全不同的两种体验。
   *
   * ## 口径
   *
   * 只有**真的缺东西**才留序号：`undefined`（调用方不传）与 `false` 都保持原样，
   * 所以别的 13 个生成页一行都不用改，页签条不会多出满屏的对勾。
   * 判据由**调用方**给（各页的必填项不同，写在本组件里就是把业务规则硬编码进来，
   * 与本组件受控的定位相悖）。
   */
  done?: boolean
}

/**
 * 生成页的页签容器 —— **取代原来的分步向导（Stepper + StepNav）。**
 *
 * 2026-09-28 用户驳回三步骨架，原话：「不需要准备素材」「这个应该 2-3 tab」。
 * 分步向导的三个真问题：
 *   1. 「准备素材」这一步的字段（产品名 / 卖点 / 参考图）本来就该跟着配置一起看，
 *      拆出来只让人多点一次「下一步」；
 *   2. 底部「上一步 / 下一步」把可提交状态藏起来了 —— 用户在第 1 步看不到价格，
 *      在第 2 步看不到生成按钮，得先"走完流程"才知道要花多少钱；
 *   3. 页签能横向比较、能直接跳，比线性向导更符合"我知道自己要改哪一项"的场景。
 *
 * 现在：顶部一行页签（可任意点）、内容区。**提交栏不在这个组件里** ——
 * 它 2026-10-08 起挂在右栏顶部的 `.studio__dock`（用户口径「每一个页面的
 * [生成按钮] 都放在右上角，[价格] 为同一行」），因此本组件曾经有的 `footer`
 * 底部槽**整个下线**：9 个页面都不再往页签底部挂东西，留着那个槽就是留着
 * 一个「谁都能再塞一个提交栏进去」的位置（那就又是两份提交栏）。
 *
 * 校验不过时给出「还差什么」而不是禁用整个按钮不留话（那句话跟着提交栏走，
 * 现在显示在右栏顶部按钮的下方）。
 *
 * 受控组件：当前页签由父组件持有 —— 各页的必填项与提交条件不同，
 * 放在父组件才不会把校验规则硬编码进组件。
 */
export function WorkflowTabs({
  tabs,
  current,
  onChange,
  children,
}: {
  tabs: WorkflowTab[]
  current: number
  onChange: (index: number) => void
  children: ReactNode
}) {
  return (
    <div className="wf-tabs">
      <div className="wf-tabs__bar" role="tablist" aria-label="配置页签">
        {tabs.map((t, i) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`wf-tab-${t.id}`}
            aria-selected={i === current}
            aria-controls={`wf-panel-${t.id}`}
            className="wf-tabs__tab"
            data-active={i === current}
            title={t.desc ?? t.title}
            onClick={() => onChange(i)}
          >
            {/*
              序号 / 对勾。`data-done` 走属性而不是类名：验收脚本与本项目其它地方
              都按 `data-*` 认状态（见 `.vtpl` 的 `data-active`）。
              `|| undefined` 与 `.vtpl` 的 `data-custom` 同一条理由 ——
              `data-done={false}` 会渲染成 `data-done="false"`，属性**照样在**，
              选择器 `[data-done]` 就把没完成的页签也一起框进去了。
            */}
            <span className="wf-tabs__num" data-done={t.done || undefined}>
              {t.done ? <Icon name="check" size={12} strokeWidth={2.8} /> : i + 1}
            </span>
            <span className="wf-tabs__label">{t.title}</span>
          </button>
        ))}
      </div>

      <div className="wf-tabs__body" role="tabpanel" id={`wf-panel-${tabs[current]?.id ?? ''}`}>
        {children}
      </div>
    </div>
  )
}
